/* StyleProfilesPage — full CRUD for saved Style Profiles.
 *
 * One profile bundles:
 *   - Name (unique per user)
 *   - Optional client binding (or "Global")
 *   - Reference post (style template)
 *   - Extra instructions (rules)
 *   - Caption length preset (auto / fixed N words / per-platform)
 *
 * Reused via the StyleProfileSelector dropdown on Generate Posts and the
 * AI Post Manager.
 */
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Sparkles, Plus, Edit2, Trash2, Loader2, Search,
} from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_FORM = {
  name: '',
  client_id: null,
  style_example: '',
  extra_instructions: '',
  caption_length_mode: 'auto',
  caption_length_words: 100,
};

const CAPTION_MODE_LABEL = {
  auto: 'Auto',
  fixed: 'Fixed words',
  per_platform: 'Per-platform smart',
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' });
  } catch { return iso; }
};

const StyleProfilesPage = () => {
  const { api } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // null = closed; {} = new; {...} = edit
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        api.get('/style-profiles'),
        api.get('/clients'),
      ]);
      setProfiles(p.data?.profiles || []);
      setClients(c.data?.clients || []);
    } catch (_) {
      toast.error('Failed to load profiles');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing({});
    setForm(DEFAULT_FORM);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      client_id: p.client_id || null,
      style_example: p.style_example || '',
      extra_instructions: p.extra_instructions || '',
      caption_length_mode: p.caption_length_mode || 'auto',
      caption_length_words: p.caption_length_words || 100,
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      client_id: form.client_id || null,
      style_example: form.style_example?.trim() || null,
      extra_instructions: form.extra_instructions?.trim() || null,
      caption_length_mode: form.caption_length_mode,
      caption_length_words:
        form.caption_length_mode === 'fixed' ? form.caption_length_words : null,
    };
    try {
      if (editing && editing.id) {
        const r = await api.put(`/style-profiles/${editing.id}`, payload);
        setProfiles((prev) => prev.map((x) => (x.id === r.data.id ? r.data : x)));
        toast.success('Profile updated');
      } else {
        const r = await api.post('/style-profiles', payload);
        setProfiles((prev) => [...prev, r.data].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success('Profile created');
      }
      setEditing(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/style-profiles/${p.id}`);
      setProfiles((prev) => prev.filter((x) => x.id !== p.id));
      toast.success('Deleted');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const clientName = (cid) => clients.find((c) => c.id === cid)?.name || cid;

  const filtered = profiles.filter((p) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      p.name.toLowerCase().includes(s) ||
      (p.style_example || '').toLowerCase().includes(s) ||
      (p.extra_instructions || '').toLowerCase().includes(s)
    );
  });

  return (
    <div className="space-y-6" data-testid="style-profiles-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-indigo-600" />
            Style Profiles
          </h1>
          <p className="text-sm text-zinc-600 mt-1">
            Save reference posts + custom rules per client (or globally). Apply with one click on any AI form.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="new-style-profile-btn">
          <Plus className="w-4 h-4 mr-2" />
          New profile
        </Button>
      </div>

      <Card>
        <CardContent className="p-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              placeholder="Search by name or content…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="style-profiles-search"
            />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-zinc-500">
            <Sparkles className="w-10 h-10 mx-auto text-zinc-300 mb-2" />
            {profiles.length === 0
              ? 'No style profiles yet. Create one to reuse your favorite reference posts.'
              : 'No profiles match your search.'}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="style-profiles-list">
          {filtered.map((p) => (
            <Card key={p.id} className="border-zinc-200 hover:border-indigo-200 transition" data-testid={`style-profile-card-${p.id}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between gap-2">
                  <span className="truncate">{p.name}</span>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`edit-style-profile-${p.id}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => handleDelete(p)}
                      data-testid={`delete-style-profile-${p.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardTitle>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  {p.client_id ? (
                    <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px]">
                      Client: {clientName(p.client_id)}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-zinc-50 text-zinc-600 border-zinc-200 text-[10px]">
                      Global
                    </Badge>
                  )}
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
                    {CAPTION_MODE_LABEL[p.caption_length_mode] || p.caption_length_mode}
                    {p.caption_length_mode === 'fixed' && p.caption_length_words ? ` · ~${p.caption_length_words}w` : ''}
                  </Badge>
                  <span className="text-[10px] text-zinc-400">Updated {fmtDate(p.updated_at)}</span>
                </div>
              </CardHeader>
              <CardContent className="pt-2 space-y-2">
                {p.style_example && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Reference post</p>
                    <p className="text-xs text-zinc-700 line-clamp-3 whitespace-pre-wrap">{p.style_example}</p>
                  </div>
                )}
                {p.extra_instructions && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Extra instructions</p>
                    <p className="text-xs text-zinc-700 line-clamp-2 whitespace-pre-wrap">{p.extra_instructions}</p>
                  </div>
                )}
                {!p.style_example && !p.extra_instructions && (
                  <p className="text-xs text-zinc-400 italic">No content — just a caption-length preset.</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit/Create dialog */}
      <Dialog open={editing !== null} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="style-profile-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit profile' : 'New style profile'}</DialogTitle>
            <DialogDescription>
              Save once, apply with one click on any AI generation form.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="sp-name">Name *</Label>
              <Input
                id="sp-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Founder voice, Educational thread style"
                maxLength={120}
                data-testid="style-profile-name-input"
              />
            </div>

            <div>
              <Label>Scope</Label>
              <Select
                value={form.client_id || 'none'}
                onValueChange={(v) => setForm({ ...form, client_id: v === 'none' ? null : v })}
              >
                <SelectTrigger data-testid="style-profile-client-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Global (any client)</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>Client: {c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-zinc-500 mt-1">
                Global profiles show up for every client; client-scoped profiles only appear when generating for that client.
              </p>
            </div>

            <div>
              <Label>Caption length preset</Label>
              <Select
                value={form.caption_length_mode}
                onValueChange={(v) => setForm({ ...form, caption_length_mode: v })}
              >
                <SelectTrigger data-testid="style-profile-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (short / platform-neutral)</SelectItem>
                  <SelectItem value="fixed">Fixed word count</SelectItem>
                  <SelectItem value="per_platform">Per-platform smart defaults</SelectItem>
                </SelectContent>
              </Select>
              {form.caption_length_mode === 'fixed' && (
                <div className="mt-2">
                  <Label>Target words</Label>
                  <Input
                    type="number"
                    min={20}
                    max={500}
                    step={10}
                    value={form.caption_length_words || ''}
                    onChange={(e) => setForm({ ...form, caption_length_words: Number.parseInt(e.target.value, 10) || null })}
                    data-testid="style-profile-words-input"
                  />
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="sp-style">Reference post (style template)</Label>
              <Textarea
                id="sp-style"
                rows={6}
                value={form.style_example}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm({ ...form, style_example: v.length > 4000 ? v.slice(0, 4000) : v });
                }}
                placeholder="Paste a winning LinkedIn / Instagram / blog post. The AI mirrors its STRUCTURE (hooks, paragraph rhythm, lists, CTA style) — not its words."
                className="text-xs"
                data-testid="style-profile-style-textarea"
              />
              <p className="text-[10px] text-zinc-500 mt-1">{form.style_example.length}/4000</p>
            </div>

            <div>
              <Label htmlFor="sp-extra">Extra instructions</Label>
              <Textarea
                id="sp-extra"
                rows={3}
                value={form.extra_instructions}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm({ ...form, extra_instructions: v.length > 1000 ? v.slice(0, 1000) : v });
                }}
                placeholder='Required CTAs, banned words, brand quirks. Example: "Always end with: Book a free call at posttosocial.com. No emojis at the start of paragraphs."'
                className="text-xs"
                data-testid="style-profile-extra-textarea"
              />
              <p className="text-[10px] text-zinc-500 mt-1">{form.extra_instructions.length}/1000</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="style-profile-save-btn">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {editing?.id ? 'Save changes' : 'Create profile'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StyleProfilesPage;
export { StyleProfilesPage };
